// Creators (/creators here, /pages/creators on Shopify, 2026-10-14): the sample request form. Three short steps, checked
// as you go, then one POST to the PRODA Creators service (CREATORS.endpoint in catalog.js), which files the request for
// the team's dashboard and, when connected, opens an Intercom ticket for the conversation. If the service can't be
// reached, the form hands the same answers to the creator's own email instead and says nothing was sent.
// Like js/links.js this imports only ui.js, catalog.js and privacy.js: the theme swaps scratch.js for the live sign-up
// module, and a failed import would stop the whole page. Analytics events carry no names, emails or addresses, and
// nothing is sent for a browser that opted out or sends Global Privacy Control. Nothing is kept in the browser.
import { $, $$, initChrome } from 'v2/ui.js';
import { CREATORS, FLAVORS, PACKS } from 'v2/catalog.js';
import { optedOut } from 'v2/privacy.js';

initChrome();

const track = (event, props = {}) => {
  if (!optedOut()) {
    try { window.posthog?.capture?.(event, props); } catch { /* analytics never break the page */ }
  }
  document.dispatchEvent(new CustomEvent('proda:track', { detail: { event, ...props } }));
};

// Samples ship where the store ships: the contiguous US and DC.
const STATES = ['AL', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
const HANDLE_FIELD = { TikTok: 'tiktok', Instagram: 'instagram', YouTube: 'youtube', Other: 'other' };

const form = $('[data-cr-form]');
const steps = $$('[data-step]', form);
const tabs = $$('[data-step-tab]');
const errorBox = $('[data-apply-error]');
const startedAt = Date.now();
let current = 1;
let begun = false;

$('[data-states]').insertAdjacentHTML('beforeend', STATES.map((s) => `<option>${s}</option>`).join(''));
$('[data-flavor-chips]').innerHTML = [...FLAVORS, ...PACKS]
  .map((f) => `<label class="chip-check f-${f.id}"><input type="checkbox" name="flavors" value="${f.full || f.name}"><span>${f.name}</span></label>`)
  .join('') + '<label class="chip-check"><input type="checkbox" name="flavors" value="Surprise me"><span>Surprise me</span></label>';

$$('[data-cr-cta]').forEach((el) => el.addEventListener('click', () => track('creator_request_cta', { placement: el.dataset.crCta })));
form.addEventListener('focusin', () => {
  if (!begun) { begun = true; track('creator_request_started'); }
});

/* ---------- The main platform's handle is the one that's required ---------- */
function syncPlatform() {
  const platform = form.elements.platform.value;
  $$('[data-handle]', form).forEach((wrap) => {
    const main = wrap.dataset.handle === platform;
    const input = $('input', wrap);
    input.required = main;
    wrap.classList.toggle('is-main', main);
    $('[data-optional]', wrap).hidden = main;
    if (!main && input.getAttribute('aria-invalid')) showError(input, '');
  });
}
$('[data-platforms]').addEventListener('change', syncPlatform);

/* ---------- Checking a step ---------- */
const MESSAGES = {
  firstName: 'Add your first name.',
  lastName: 'Add your last name.',
  email: 'Use an email address like name@example.com.',
  phone: 'Add a phone number with area code, or leave it blank.',
  platform: 'Choose where you post the most.',
  tiktok: 'Add your TikTok handle.',
  instagram: 'Add your Instagram handle.',
  youtube: 'Add your YouTube channel.',
  other: 'Add a link to where you post.',
  followers: 'Choose a rough follower count.',
  bestPost: 'Use a full link, starting with https://',
  address1: 'Add a street address.',
  city: 'Add a city.',
  state: 'Choose a state.',
  zip: 'Use a 5-digit ZIP code.',
  adult: 'Please confirm you’re 18 or older with a US address.',
};
const phoneOk = (v) => v.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '').length === 10;

function fieldError(input) {
  if (input.name === 'phone') return input.value.trim() && !phoneOk(input.value) ? MESSAGES.phone : '';
  if (input.type === 'radio') return form.elements[input.name].value ? '' : MESSAGES[input.name];
  return input.checkValidity() ? '' : MESSAGES[input.name] || input.validationMessage;
}
function errorHost(input) {
  return input.type === 'radio' ? input.closest('fieldset') : input.closest('.field');
}
function showError(input, message) {
  const wrap = errorHost(input);
  const target = input.type === 'radio' ? wrap : input;
  let note = $(':scope > .field__error', wrap);
  if (!message) {
    target.removeAttribute('aria-invalid');
    note?.remove();
    return;
  }
  target.setAttribute('aria-invalid', 'true');
  if (!note) {
    note = document.createElement('p');
    note.className = 'field__error';
    note.id = `${input.id || input.name}-error`;
    wrap.append(note);
    if (input.type !== 'radio') input.setAttribute('aria-describedby', [input.getAttribute('aria-describedby'), note.id].filter(Boolean).join(' '));
  }
  note.textContent = message;
}
function checkStep(n) {
  let first = null;
  const seen = new Set();
  $$('input[required], select[required], textarea[required], input[name="phone"], input[name="bestPost"]', steps[n - 1]).forEach((input) => {
    if (input.type === 'radio') {
      if (seen.has(input.name)) return;
      seen.add(input.name);
    }
    const message = fieldError(input);
    showError(input, message);
    if (message && !first) first = input;
  });
  first?.focus();
  return !first;
}
// Once a field has been flagged, it clears as soon as it's fixed.
const recheck = (e) => {
  const input = e.target;
  const flagged = input.type === 'radio' ? errorHost(input)?.getAttribute('aria-invalid') : input.getAttribute('aria-invalid');
  if (flagged) showError(input, fieldError(input));
};
form.addEventListener('input', recheck);
form.addEventListener('change', recheck);

/* ---------- Moving between steps ---------- */
function go(n) {
  current = n;
  steps.forEach((s, i) => { s.hidden = i !== n - 1; });
  tabs.forEach((t, i) => {
    if (i === n - 1) t.setAttribute('aria-current', 'step'); else t.removeAttribute('aria-current');
    t.classList.toggle('is-done', i < n - 1);
  });
  const card = form.closest('.apply-card');
  if (card.getBoundingClientRect().top < 80) card.scrollIntoView({ block: 'start' });
  $('input:not([type="hidden"]), select, textarea', steps[n - 1])?.focus({ preventScroll: true });
  track('creator_request_step', { step: n });
}
form.addEventListener('click', (e) => {
  if (e.target.closest('[data-next]') && checkStep(current)) go(current + 1);
  if (e.target.closest('[data-back]')) go(current - 1);
});
// Finished steps can be revisited from the step list.
tabs.forEach((t, i) => t.addEventListener('click', () => { if (i < current - 1) go(i + 1); }));

/* ---------- Sending ---------- */
// Where the visit came from: UTM tags, a ref, and the referring site (origin and path only).
function source() {
  const q = new URLSearchParams(location.search);
  const out = { page: location.pathname };
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref'].forEach((k) => { if (q.get(k)) out[k] = q.get(k).slice(0, 120); });
  try {
    if (document.referrer) {
      const r = new URL(document.referrer);
      if (r.host !== location.host) out.referrer = `${r.origin}${r.pathname}`.slice(0, 200);
    }
  } catch { /* no referrer */ }
  return out;
}
function answers() {
  const data = new FormData(form);
  const one = (k) => String(data.get(k) || '').trim();
  return {
    firstName: one('firstName'), lastName: one('lastName'), email: one('email'), phone: one('phone'),
    platform: one('platform'), tiktok: one('tiktok'), instagram: one('instagram'), youtube: one('youtube'), other: one('other'),
    followers: one('followers'), tiktokShop: one('tiktokShop'), niches: data.getAll('niches'), bestPost: one('bestPost'),
    flavors: data.getAll('flavors'), pitch: one('pitch'),
    address1: one('address1'), address2: one('address2'), city: one('city'), state: one('state'), zip: one('zip'),
    heard: one('heard'), adult: one('adult') === 'yes',
    company_site: one('company_site'), startedAt, source: source(),
  };
}
// The request as plain text, for the email fallback.
function asText(a) {
  const rows = [
    ['Name', `${a.firstName} ${a.lastName}`],
    ['Email', a.email],
    ['Phone', a.phone],
    ['Posts most on', a.platform === 'Other' ? 'Somewhere else' : a.platform],
    ['TikTok', a.tiktok], ['Instagram', a.instagram], ['YouTube', a.youtube], ['Other link', a.other],
    ['Followers', a.followers],
    ['TikTok Shop affiliate', a.tiktokShop],
    ['Makes', a.niches.join(', ')],
    ['A post I’m proud of', a.bestPost],
    ['Flavors', a.flavors.join(', ')],
    ['What I’d make', a.pitch],
    ['Ship to', [a.address1, a.address2, `${a.city}, ${a.state} ${a.zip}`].filter(Boolean).join(', ')],
    ['Heard about PRODA', a.heard],
    ['18 or older with a US address', a.adult ? 'Yes' : 'No'],
  ];
  return `Creator sample request\n\n${rows.filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join('\n')}\n`;
}

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

function done({ kicker, title, lede, ref, actions }) {
  form.hidden = true;
  $('[data-apply-steps]').hidden = true;
  const box = $('[data-apply-done]');
  $('[data-done-kicker]', box).textContent = kicker;
  $('[data-done-title]', box).textContent = title;
  $('[data-done-lede]', box).textContent = lede;
  $('.apply-done__ref', box)?.remove();
  if (ref) $('[data-done-lede]', box).insertAdjacentHTML('afterend', `<p class="apply-done__ref">Your request number is <b>${esc(ref)}</b>.</p>`);
  $('[data-done-actions]', box).innerHTML = actions;
  box.hidden = false;
  box.focus();
  box.scrollIntoView({ block: 'center' });
}

form.addEventListener('submit', async (e) => {
  e.preventDefault();
  errorBox.hidden = true;
  for (let n = 1; n <= steps.length; n++) {
    if (!checkStep(n)) { if (n !== current) { go(n); checkStep(n); } return; }
  }
  const a = answers();
  if (a.company_site) return; // a bot filled the hidden field
  const button = $('[data-submit]', form);
  button.disabled = true;
  button.textContent = 'Sending…';
  let res = null;
  try {
    res = await fetch(CREATORS.endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(a) });
  } catch { /* offline, or the service can't be reached */ }
  const reply = res ? await res.json().catch(() => null) : null;

  if (res?.ok && reply?.ok) {
    track('creator_request_submitted', { platform: a.platform, followers: a.followers, flavors: a.flavors.length, tiktok_shop: a.tiktokShop || 'unanswered' });
    done({
      kicker: 'Request received',
      title: `Thanks, ${a.firstName}.`,
      lede: `We read every request. If it’s a fit, we’ll email ${a.email} before anything ships.`,
      ref: reply.id,
      actions: `<a class="btn btn--ink" href="${CREATORS.instagram}" target="_blank" rel="noopener">Follow @drinkproda</a><a class="btn btn--ghost" href="/collections/all">Shop PRODA</a>`,
    });
    return;
  }
  if (res?.status === 400 && reply?.errors) {
    // The service's checks: show them where they belong.
    let firstStep = null;
    Object.entries(reply.errors).forEach(([name, message]) => {
      const el = form.elements[name];
      const input = el instanceof RadioNodeList ? el[0] : el;
      if (!input?.closest) return;
      showError(input, message);
      const n = Number(input.closest('[data-step]').dataset.step);
      firstStep = firstStep ? Math.min(firstStep, n) : n;
    });
    button.disabled = false;
    button.textContent = 'Request samples';
    if (firstStep) go(firstStep);
    return;
  }
  // The service didn't answer (or failed): the creator sends the same answers from their own email.
  track('creator_request_fallback', { status: res?.status || 0 });
  const subject = `Creator sample request: ${a.firstName} ${a.lastName}`;
  const mailto = `mailto:${CREATORS.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(asText(a))}`;
  done({
    kicker: 'One more step',
    title: 'Send it from your email.',
    lede: `We couldn’t file your request from here, so it’s ready to send from your own email to ${CREATORS.email}. Nothing has been sent yet.`,
    actions: `<a class="btn btn--orange" href="${esc(mailto)}">Email my request</a><button class="btn btn--ghost" type="button" data-copy>Copy my answers</button>`,
  });
  $('[data-copy]')?.addEventListener('click', async (ev) => {
    try { await navigator.clipboard.writeText(asText(a)); ev.target.textContent = 'Copied'; } catch { ev.target.textContent = 'Copy failed'; }
  });
});
